<script setup lang="ts">
import { computed } from "vue";
import { syncer } from "@/repositories/Syncer";

const trackers = computed(() => syncer.syncTrackers.value);

const globalStatus = computed(() => {
  if (
    trackers.value.length === 0 ||
    trackers.value.every((t) => t.status === "done")
  ) {
    return "done";
  }
  if (trackers.value.every((t) => t.status === "failed")) {
    return "failed";
  }
  return "syncing";
});

function remove(id: string): void {
  syncer.removeSyncTracker(id);
}
</script>

<template>
  <div
    v-if="trackers.length"
    class="sync-toast p-3 shadow-lg rounded border border-1"
  >
    <div class="d-flex gap-4 align-items-start">
      <div
        v-if="globalStatus === 'failed'"
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2 border border-3 border-danger"
      >
        <el-icon size="1.5rem"><i class="bi-x-lg text-danger"></i></el-icon>
      </div>
      <div
        v-else-if="globalStatus === 'done'"
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2 border border-3 border-success"
      >
        <el-icon size="1.5rem"
          ><i class="bi-check-lg text-success"></i
        ></el-icon>
      </div>
      <div
        v-else
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2"
      >
        <el-icon size="1.5rem"><i class="bi-cloud-arrow-up"></i></el-icon>
        <div
          class="spinner-border text-primary position-absolute"
          style="width: 3rem; height: 3rem"
          role="status"
        ></div>
      </div>
      <div class="d-flex flex-column gap-1">
        <span class="fw-bold">{{ $t("dashboard:sync.toast.title") }}</span>
        <span style="font-size: 0.8rem" class="text-secondary">{{
          $t("dashboard:sync.toast.message")
        }}</span>
      </div>
    </div>
    <el-collapse class="mt-3">
      <el-collapse-item>
        <template #title>
          <span class="fw-normal" style="font-size: 0.8rem">{{
            `${$t("details")} (${trackers.length} ${
              trackers.length > 1 ? $t("files") : $t("file")
            })`
          }}</span>
        </template>
        <ul class="list-group w-100">
          <li
            v-for="tracker in trackers"
            :key="tracker.id"
            class="d-inline-flex justify-content-between align-items-center py-1 row"
          >
            <div class="col-8 d-flex align-items-center gap-1">
              <el-icon size="1rem" class="text-dark col-auto">
                <i class="bi bi-file-earmark"></i>
              </el-icon>
              <span class="col text-truncate">{{ tracker.drawingName }}</span>
            </div>
            <div class="col align-items-center justify-content-end d-flex gap-2">
              <i
                v-if="tracker.status === 'done'"
                class="bi bi-check-lg h4 text-success d-inline-flex align-items-center m-0"
              ></i>
              <span v-else-if="tracker.status === 'failed'" class="text-danger small">
                {{ tracker.error || "Erreur" }}
              </span>
              <span v-else class="spinner-border spinner-border-sm text-secondary"></span>
              <button
                v-if="tracker.status === 'failed'"
                class="btn-close"
                @click="remove(tracker.id)"
              ></button>
            </div>
          </li>
        </ul>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<style lang="scss">
.sync-toast {
  background-color: var(--el-bg-color);
  border-color: var(--bs-border-color) !important;
  color: var(--bs-body-color);

  .el-collapse {
    border-bottom: 0 !important;
    border-top: 0 !important;
    --el-collapse-header-bg-color: transparent;
    --el-collapse-content-bg-color: transparent;
  }

  .el-collapse-item__wrap {
    border-bottom: 0 !important;
    background-color: transparent;
  }

  .el-collapse-item__header {
    border-bottom: 0 !important;
    height: 1.8rem !important;
    background-color: transparent;
    color: var(--bs-body-color);
  }

  .el-collapse-item__content {
    background-color: transparent;
    color: var(--bs-body-color);
  }

  .el-collapse-item__arrow {
    color: var(--bs-body-color);
  }
}
</style>
